import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "primereact/button";
import ContainerHeader from "../components/containerHeader";
import SearchProductList from "../components/searchProductList";
import useEcomodClasses from "../hooks/useEcomodClasses";
import useSupClasses from "../hooks/useSupClasses";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { Family } from "../api/models/fullProductData.models";
import AkeneoProductView from "../views/akeneoProductView";
import useSearchProducts from "../hooks/useSearchProducts";
import { SearchProduct } from "../api/models/searchProduct.models";
import useImportProductsFromExcel from "../hooks/useImportProductsFromExcel";
import ImportProductsDialog from "../dialogs/importProductsDialog";
import TitleComponent from "../components/title";
import useExcel from "../hooks/useExcel";
import { useSearchParams } from "react-router";
import { InfoDot } from "../components/infoDot";
import { useAuth } from "../contexts/authContext";
import { useDebounce } from "primereact/hooks";

const PaddedContainer = styled.div`
  padding: 2rem 4rem 0 2rem;
  text-align: left;
  flex-grow: 1;
`;

const DownloadLink = styled.span`
  color: var(--sumi-blue);
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
  margin-top: 5px;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid var(--sumi-blue);
    outline-offset: 2px;
  }
`;

const Description = styled.p`
  font-size: 0.9rem;
  max-width: 700px;
  padding: 1rem 2rem 0 2rem;
  text-align: left;
  color: var(--text-color-secondary);
`;

const Models: React.FC = () => {
  const { t } = useTranslation();
  const { isProductCreator } = useAuth();
  const [companyId, companyName] = useUIStore(
    useShallow((state: UIState) => [state.companyId, state.companyName]),
  );
  const [searchParams] = useSearchParams();
  const searchProductId = searchParams.get("searchProductId");
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [selectedSearchProduct, setSelectedSearchProduct] =
    useState<SearchProduct | null>(null);
  const [productPage, setProductPage] = useState<number>(1);
  const [showProductDialog, setShowProductDialog] = useState<boolean>(false);
  const [showImportProductExcelDialog, setShowImportProductExcelDialog] =
    useState<boolean>(false);
  const [searchValue, debouncedSearchValue, setSearchValue] =
    useDebounce<string>("", 800);
  const [sortBy, setSortBy] = useState<string>("created");
  const [asc, setAsc] = useState<boolean>(false);

  const isSearching = !!debouncedSearchValue;

  const { searchProductsData, isLoadingSearchProduct, refetchSearchProducts } =
    useSearchProducts({
      page: isSearching ? undefined : productPage,
      companyId: companyId as number,
      limit: isSearching ? undefined : 20,
      searchStrings: isSearching ? [debouncedSearchValue] : undefined,
      sortBy,
      asc,
    });

  useEffect(() => {
    setProductPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (companyId) {
      refetchSearchProducts();
    }
  }, [companyId, refetchSearchProducts]);

  useEffect(() => {
    if (searchProductId && searchProductsData) {
      // Remove searchProductId from URL params
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("searchProductId");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${newSearchParams}`,
      );

      const foundProduct = searchProductsData.data.find(
        (product) => product.identifier === searchProductId,
      );
      if (foundProduct) {
        setSelectedSearchProduct(foundProduct);
        setShowProductDialog(true);
      }
    }
  }, [searchProductId, searchProductsData]);

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const { downloadExcelTemplate } = useImportProductsFromExcel();

  const { exportExcel } = useExcel();

  const isLoading =
    isLoadingSearchProduct || isLoadingEcomodClasses || isLoadingSupClasses;

  const handleSelectItem = (item: SearchProduct) => {
    setSelectedSearchProduct(item);
    if (item.family === Family.product) {
      setShowProductDialog(true);
    }
  };

  const handleDownloadExcelTemplate = async () => {
    await downloadExcelTemplate();
  };

  const handleExport = async () => {
    setIsExporting(true);
    await exportExcel();
    setIsExporting(false);
  };

  const handleSortChange = (nextSortBy: string, nextAsc: boolean) => {
    setSortBy(nextSortBy);
    setAsc(nextAsc);
    setProductPage(1);
  };

  return (
    <>
      <ContainerHeader
        title={t("header-models")}
        description={t("subheader-models")}
        additionalLinkText={t("subheader-models-link")}
      />
      <TitleComponent
        text={t("header-models-of-company", { company: companyName })}
        smallPadding
      />
      <Description>{t("description-models")}</Description>

      {(showProductDialog || selectedSearchProduct) && (
        <AkeneoProductView
          onHide={() => {
            setShowProductDialog(false);
            setSelectedSearchProduct(null);
          }}
          ecomodClasses={ecomodClasses || []}
          supClasses={supClasses || []}
          selectedSearchProduct={selectedSearchProduct as SearchProduct}
          refetchSearchProducts={refetchSearchProducts}
        />
      )}
      {showImportProductExcelDialog && (
        <ImportProductsDialog
          show={showImportProductExcelDialog}
          onHide={() => {
            setShowImportProductExcelDialog(false);
          }}
          onAccept={() => {
            setShowImportProductExcelDialog(false);
          }}
          onImportComplete={refetchSearchProducts}
        />
      )}
      {!showProductDialog && !selectedSearchProduct && (
        <PaddedContainer>
          <div style={{ display: "flex", gap: "1rem" }}>
            <InfoDot
              position="right"
              text={t("infotext-create-new-product")}
              marginLeft="10px"
            >
              <Button
                id="create-product-button"
                label={t("cmd-create-new-product")}
                onClick={() => {
                  setShowProductDialog(true);
                }}
                disabled={!isProductCreator}
              />
            </InfoDot>
            <InfoDot
              position="right"
              text={t("infotext-export-excel")}
              marginLeft="10px"
            >
              <Button
                id="export-excel-button"
                type="button"
                label={t("cmd-export-to-file")}
                onClick={handleExport}
                style={{ marginBottom: "auto" }}
                outlined
                loading={isExporting}
                disabled={!isProductCreator}
              />
            </InfoDot>

            <InfoDot
              position="right"
              text={t("infotext-import-excel")}
              marginLeft="10px"
            >
              <Button
                id="import-excel-button"
                type="button"
                label={t("cmd-load-from-file")}
                onClick={() => setShowImportProductExcelDialog(true)}
                style={{ marginBottom: "auto" }}
                outlined
                disabled={!isProductCreator}
              />
            </InfoDot>

            <DownloadLink
              role="button"
              tabIndex={0}
              onClick={handleDownloadExcelTemplate}
              onKeyDown={(e) =>
                e.key === "Enter" && handleDownloadExcelTemplate()
              }
            >
              {t("cmd-download-excel-product-template")}
            </DownloadLink>
          </div>

          <div>
            <SearchProductList
              searchProductsData={searchProductsData?.data || []}
              handleSelectItem={handleSelectItem}
              totalRecords={
                isSearching
                  ? undefined
                  : (searchProductsData?._count ??
                    searchProductsData?.data.length)
              }
              page={isSearching ? undefined : productPage}
              rows={isSearching ? undefined : 20}
              onPageChange={isSearching ? undefined : setProductPage}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              isLoading={isLoading}
              sortBy={sortBy}
              asc={asc}
              onSortChange={handleSortChange}
            />
          </div>
        </PaddedContainer>
      )}
    </>
  );
};

export default Models;
